import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Action, Subject } from '@erp/authz';
import { rpcClient } from 'src/api/rpc/client';
import { useCan } from 'src/hooks/useCan';

/**
 * The task's resolved playbook (`tasks.getPlaybook`), including the procedure
 * versions the task locked on first activation. Read-only: never pins.
 */
export function useTaskPlaybook(taskId: string | null | undefined) {
  const [canReadTasks] = useCan(Action.Read, Subject.Task);
  return useQuery({
    queryKey: ['task-playbook', taskId],
    queryFn: () => rpcClient.tasks.getPlaybook({ id: taskId ?? '' }),
    enabled: canReadTasks && !!taskId,
  });
}

/**
 * Release a procedure's locked version (`tasks.releaseProcedureLock`) so the
 * task's next attempt pins the version the procedure resolves to now.
 */
export function useReleaseTaskProcedureLock(taskId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sopId: string) =>
      rpcClient.tasks.releaseProcedureLock({ taskId, sopId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['task-playbook', taskId],
      });
      void queryClient.invalidateQueries({
        queryKey: ['task-timeline', taskId],
      });
    },
  });
}
